/**
 * B2 · CLASSROOM CHAT — PURE selectors (testable without a database).
 *
 * What lives here is what the UI needs to compute and is NOT authorization:
 * grouping by day, unread against one's own marker, reaction aggregate, and
 * derived "last seen".
 *
 * Presence (§7a of the plan) is DERIVED from what's already synced: each
 * person's last message and their read marker. No heartbeat, no table, no
 * periodic write — 300 students typing every 30s would be ~15,000 row
 * deliveries per minute for data that expires in half a minute.
 */

export interface ChatMessageLike {
  id: string;
  author_id?: string | null;
  created_at?: string | null;
  hidden_at?: string | null;
  pinned_at?: string | null;
}

export interface ChatReactionLike {
  message_id?: string | null;
  user_id?: string | null;
  emoji?: string | null;
}

/** One day of conversation: the ISO key (`YYYY-MM-DD`) and its messages. */
export interface ChatDayGroup<T extends ChatMessageLike> {
  day: string;
  messages: T[];
}

/**
 * Groups messages by LOCAL day, preserving chronological order.
 *
 * The cutoff is by local day and not UTC on purpose: a chat's "today" header
 * has to match the reader's calendar, not Greenwich's.
 */
export function groupMessagesByDay<T extends ChatMessageLike>(
  messages: readonly T[],
): ChatDayGroup<T>[] {
  const out: ChatDayGroup<T>[] = [];
  for (const m of messages) {
    const day = localDay(m.created_at);
    const last = out.at(-1);
    if (last && last.day === day) {
      last.messages.push(m);
      continue;
    }
    out.push({ day, messages: [m] });
  }
  return out;
}

/** `YYYY-MM-DD` key in local zone; empty string if there's no usable date. */
function localDay(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const mm = `${d.getMonth() + 1}`.padStart(2, "0");
  const dd = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Unread messages for a person: those AFTER their read marker and written
 * by SOMEONE ELSE.
 *
 * With no marker (`lastReadAt` null), counts everything from others: it's the first visit to the
 * classroom, and saying "0 unread" there would hide the entire conversation.
 */
export function countUnreadMessages(
  messages: readonly ChatMessageLike[],
  userId: string,
  lastReadAt: string | null | undefined,
): number {
  const since = lastReadAt ? Date.parse(lastReadAt) : Number.NEGATIVE_INFINITY;
  let unread = 0;
  for (const m of messages) {
    if (m.author_id === userId) {
      continue;
    }
    const at = m.created_at ? Date.parse(m.created_at) : Number.NaN;
    if (!Number.isNaN(at) && at > since) {
      unread += 1;
    }
  }
  return unread;
}

/** Reaction count for ONE message: emoji → count, and which one I set. */
export interface ReactionSummary {
  counts: { emoji: string; count: number }[];
  mine: string | null;
}

/**
 * Aggregates a message's reactions. Output order is by descending
 * count and, on ties, alphabetical: without the tiebreak, two devices
 * would render the same emojis in a different order and the row would
 * "dance" on sync.
 */
export function summarizeReactions(
  reactions: readonly ChatReactionLike[],
  messageId: string,
  userId: string,
): ReactionSummary {
  const counts = new Map<string, number>();
  let mine: string | null = null;
  for (const r of reactions) {
    if (r.message_id !== messageId || !r.emoji) {
      continue;
    }
    counts.set(r.emoji, (counts.get(r.emoji) ?? 0) + 1);
    if (r.user_id === userId) {
      mine = r.emoji;
    }
  }
  return {
    counts: [...counts.entries()]
      .map(([emoji, count]) => ({ emoji, count }))
      .toSorted((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji)),
    mine,
  };
}

/**
 * §7a · DERIVED PRESENCE, zero cost: each classroom person's last known
 * activity = their most recent message. No heartbeats or periodic writes;
 * covers the feeling that "the classroom is alive" without a live channel.
 */
export function lastSeenByAuthor(messages: readonly ChatMessageLike[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const m of messages) {
    if (!m.author_id || !m.created_at) {
      continue;
    }
    const prev = out.get(m.author_id);
    if (!prev || Date.parse(m.created_at) > Date.parse(prev)) {
      out.set(m.author_id, m.created_at);
    }
  }
  return out;
}
